import * as THREE from 'three';
import type { PergolaConfig, Anclaje, ScreenType, SideKey } from '../config/types';
import { createAluminumMaterial, createConcreteMaterial, createSteelMaterial } from './materials';
import { getSurfaceMaterial } from './surfaces';

/** Secciones de los perfiles (m). */
const POST = 0.1; // poste 100×100 mm
const BEAM_W = 0.1; // ancho de viga
const BEAM_H = 0.18; // alto de viga
const LAMA_THICK = 0.025;
const LAMA_CHORD = 0.16; // cuerda (profundidad) de la lama
const LAMA_SPACING = 0.18; // paso objetivo entre lamas

export interface PergolaParts {
  group: THREE.Group;
  /** Lamas, para animar la apertura (rotación sobre X). */
  lamas: THREE.Mesh[];
  /** Material de aluminio de la estructura, para cambiar el color en vivo. */
  aluMaterial: THREE.MeshStandardMaterial;
  /** Material de aluminio de las lamas (color independiente). */
  lamaMaterial: THREE.MeshStandardMaterial;
  /** Material emisivo de la tira LED (null si no hay LED). */
  ledMaterial: THREE.MeshStandardMaterial | null;
  /** Puntos de luz del LED (para animar RGB). */
  ledLights: THREE.PointLight[];
  ledRgb: boolean;
  /** Altura total hasta la cara superior de la viga (m). */
  totalHeight: number;
}

/**
 * Construye la pérgola completa a partir de la configuración.
 * Convención de ejes: X = ancho, Z = largo, Y = altura. Base de postes en y=0.
 */
export function buildPergola(config: PergolaConfig): PergolaParts {
  const W = config.widthMm / 1000;
  const L = config.lengthMm / 1000;
  const H = config.heightMm / 1000;

  const group = new THREE.Group();
  group.name = 'pergola';

  const alu = createAluminumMaterial(config.colorHex);

  // Base de la pérgola (suelo bajo la estructura) según el anclaje.
  const padMargin = 0.5;
  const pad = new THREE.Mesh(
    new THREE.PlaneGeometry(W + padMargin * 2, L + padMargin * 2),
    getSurfaceMaterial(config.anclaje),
  );
  pad.rotation.x = -Math.PI / 2;
  pad.position.y = 0.012;
  pad.receiveShadow = true;
  pad.name = 'base-pad';
  group.add(pad);

  // Posición de postes (insertados media sección hacia dentro del perímetro).
  const px = W / 2 - POST / 2;
  const pz = L / 2 - POST / 2;

  // Lista de postes: 4 esquinas (+ 2 intermedios si postes reforzados).
  // isBack = fila trasera (lado del muro en modo adosada).
  type PostDef = { x: number; z: number; isBack: boolean };
  const posts: PostDef[] = [
    { x: -px, z: pz, isBack: false },
    { x: px, z: pz, isBack: false },
    { x: -px, z: -pz, isBack: true },
    { x: px, z: -pz, isBack: true },
  ];
  if (config.postes === 6) {
    posts.push({ x: -px, z: 0, isBack: false });
    posts.push({ x: px, z: 0, isBack: false });
  }

  const isAdosada = config.tipo === 'adosada';

  // --- Postes + anclajes ---
  const postGeo = new THREE.BoxGeometry(POST, H, POST);
  for (const p of posts) {
    if (isAdosada && p.isBack) continue; // adosada: sin postes traseros
    const post = new THREE.Mesh(postGeo, alu);
    post.position.set(p.x, H / 2, p.z);
    post.castShadow = true;
    post.receiveShadow = true;
    group.add(post);

    const anchor = createAnchor(config.anclaje);
    anchor.position.set(p.x, 0, p.z);
    group.add(anchor);
  }

  // --- Vigas perimetrales (sobre los postes) ---
  const beamY = H + BEAM_H / 2;
  // Vigas largas (eje Z) a ambos lados.
  const longBeamGeo = new THREE.BoxGeometry(BEAM_W, BEAM_H, L);
  for (const sx of [-px, px]) {
    const beam = new THREE.Mesh(longBeamGeo, alu);
    beam.position.set(sx, beamY, 0);
    beam.castShadow = true;
    beam.receiveShadow = true;
    group.add(beam);
  }
  // Vigas cortas (eje X) en frente y fondo.
  const shortBeamGeo = new THREE.BoxGeometry(W, BEAM_H, BEAM_W);
  for (const sz of [pz, -pz]) {
    const beam = new THREE.Mesh(shortBeamGeo, alu);
    beam.position.set(0, beamY, sz);
    beam.castShadow = true;
    beam.receiveShadow = true;
    group.add(beam);
  }

  // --- Lamas (rotan sobre X). Nº recalculado según el largo. ---
  const lamas: THREE.Mesh[] = [];
  const lamaMaterial = createAluminumMaterial(config.lamasColorHex);
  const innerL = L - 2 * BEAM_W;
  const lamaLen = W - 2 * BEAM_W;
  const n = Math.max(3, Math.round(innerL / LAMA_SPACING));
  const spacing = innerL / n;
  const lamaGeo = new THREE.BoxGeometry(lamaLen, LAMA_THICK, LAMA_CHORD);
  const startAngle = config.lamasOpen ? (config.lamasDir * Math.PI) / 2 : 0;
  for (let i = 0; i < n; i++) {
    const lama = new THREE.Mesh(lamaGeo, lamaMaterial);
    const z = -innerL / 2 + spacing * (i + 0.5);
    lama.position.set(0, beamY, z);
    lama.rotation.x = startAngle;
    lama.castShadow = true;
    lama.receiveShadow = true;
    lama.name = 'lama';
    group.add(lama);
    lamas.push(lama);
  }

  // (En modo adosada el muro lo aporta la casa del entorno; la pérgola se pega a ella.)

  // --- Iluminación LED perimetral (extra "led") ---
  let ledMaterial: THREE.MeshStandardMaterial | null = null;
  const ledLights: THREE.PointLight[] = [];
  if (config.ledOn) {
    const ledColor = new THREE.Color(config.ledColorHex);
    ledMaterial = new THREE.MeshStandardMaterial({
      color: 0x0a0a0a,
      emissive: ledColor,
      emissiveIntensity: 2.6,
      roughness: 1,
      metalness: 0,
      toneMapped: false, // brillo pleno (efecto neón)
    });
    const yLed = H - 0.03;
    const t = 0.04;
    const stripZ = new THREE.BoxGeometry(t, t, L - 2 * BEAM_W);
    for (const sx of [-px, px]) {
      const strip = new THREE.Mesh(stripZ, ledMaterial);
      strip.position.set(sx, yLed, 0);
      strip.name = 'led-strip';
      group.add(strip);
    }
    const stripX = new THREE.BoxGeometry(W - 2 * BEAM_W, t, t);
    for (const sz of [-pz, pz]) {
      const strip = new THREE.Mesh(stripX, ledMaterial);
      strip.position.set(0, yLed, sz);
      strip.name = 'led-strip';
      group.add(strip);
    }

    // Luces que iluminan el suelo/estructura (intensas de noche).
    const dist = Math.max(W, L) * 1.6;
    for (const sz of [-L * 0.25, L * 0.25]) {
      const light = new THREE.PointLight(ledColor.clone(), 9, dist, 2);
      light.position.set(0, H - 0.18, sz);
      ledLights.push(light);
      group.add(light);
    }
  }

  // --- Cerramientos laterales (extra "screens") ---
  if (config.screensOn) {
    const sides: Array<{ key: SideKey; coord: number; halfWidth: number; rotY: number; alongZ: boolean }> = [
      { key: 'frontal', coord: pz, halfWidth: px, rotY: 0, alongZ: false },
      { key: 'trasero', coord: -pz, halfWidth: px, rotY: 0, alongZ: false },
      { key: 'izquierda', coord: -px, halfWidth: pz, rotY: Math.PI / 2, alongZ: true },
      { key: 'derecha', coord: px, halfWidth: pz, rotY: Math.PI / 2, alongZ: true },
    ];
    for (const s of sides) {
      const type = config.screens[s.key];
      if (type === 'ninguno') continue;
      if (isAdosada && s.key === 'trasero') continue; // lado del muro
      const panel = buildScreenPanel(type, s.halfWidth * 2, H, config.colorHex);
      if (s.alongZ) {
        panel.position.set(s.coord, 0, 0);
        panel.rotation.y = s.rotY;
      } else {
        panel.position.set(0, 0, s.coord);
      }
      group.add(panel);
    }
  }

  return {
    group,
    lamas,
    aluMaterial: alu,
    lamaMaterial,
    ledMaterial,
    ledLights,
    ledRgb: config.ledRgb,
    totalHeight: H + BEAM_H,
  };
}

/**
 * Panel de cerramiento lateral según el tipo. Se construye en el plano local XY
 * (mira a +Z); el llamante lo rota para los laterales. width = ancho del vano, H = altura.
 */
function buildScreenPanel(type: ScreenType, width: number, H: number, frameColorHex: string): THREE.Group {
  const g = new THREE.Group();
  g.name = `screen-${type}`;
  const frameMat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(frameColorHex),
    metalness: 0.55,
    roughness: 0.4,
  });
  const railMat = new THREE.MeshStandardMaterial({ color: 0xb9bdc2, metalness: 0.7, roughness: 0.35 });

  if (type === 'pantalla') {
    // Screen enrollable: cajón superior + tela oscura bajando ~65% de la altura.
    const fabricMat = new THREE.MeshStandardMaterial({
      color: 0x24262b,
      roughness: 0.95,
      metalness: 0,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
    });
    const fabricH = H * 0.62;
    const fabric = new THREE.Mesh(new THREE.PlaneGeometry(width - 0.06, fabricH), fabricMat);
    fabric.position.set(0, H - fabricH / 2, 0);
    g.add(fabric);
    const box = new THREE.Mesh(new THREE.BoxGeometry(width, 0.14, 0.14), frameMat);
    box.position.set(0, H - 0.07, 0);
    box.castShadow = true;
    g.add(box);
    return g;
  }

  // Vidrio muy transparente; las juntas se distinguen por el solape de hojas.
  const glassMat = new THREE.MeshStandardMaterial({
    color: 0xeaf2f7,
    metalness: 0.0,
    roughness: 0.02,
    transparent: true,
    opacity: 0.16,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const framed = type === 'correderas';
  const nPanes = framed ? Math.max(3, Math.round(width / 0.62)) : 4;
  const railTop = 0.07;
  const railBottom = 0.09;
  const paneH = H - railTop - railBottom;
  const paneY = railBottom + paneH / 2;
  const paneThick = 0.018;
  const slot = width / nPanes;
  const overlap = 0.05; // las hojas solapan → junta visible (doble vidrio)

  for (let i = 0; i < nPanes; i++) {
    const cx = -width / 2 + slot / 2 + i * slot;
    const paneW = slot + overlap;
    const zoff = framed ? (i % 2) * 0.03 : (i % 2 === 0 ? -0.012 : 0.012); // dos carriles
    const pane = new THREE.Mesh(new THREE.BoxGeometry(paneW, paneH, paneThick), glassMat);
    pane.position.set(cx, paneY, zoff);
    g.add(pane);
    if (framed) {
      for (const sx of [cx - slot / 2, cx + slot / 2]) {
        const mull = new THREE.Mesh(new THREE.BoxGeometry(0.04, paneH, 0.06), frameMat);
        mull.position.set(sx, paneY, 0);
        g.add(mull);
      }
    }
  }

  // Carril superior (bajo la viga).
  const top = new THREE.Mesh(new THREE.BoxGeometry(width, railTop, 0.12), railMat);
  top.position.set(0, H - railTop / 2, 0);
  top.castShadow = true;
  g.add(top);

  // Riel inferior en el suelo, con ranuras marcadas.
  const trackMat = new THREE.MeshStandardMaterial({ color: 0x36393f, metalness: 0.6, roughness: 0.5 });
  const grooveMat = new THREE.MeshStandardMaterial({ color: 0x14161a, metalness: 0.4, roughness: 0.8 });
  const trackBase = new THREE.Mesh(new THREE.BoxGeometry(width, railBottom, 0.16), trackMat);
  trackBase.position.set(0, railBottom / 2, 0);
  trackBase.receiveShadow = true;
  g.add(trackBase);
  for (const gz of [-0.04, 0.0, 0.04]) {
    const groove = new THREE.Mesh(new THREE.BoxGeometry(width - 0.04, 0.016, 0.012), grooveMat);
    groove.position.set(0, railBottom - 0.006, gz);
    g.add(groove);
  }
  return g;
}

/** Pieza de anclaje según el tipo seleccionado (se intercambia con la config). */
function createAnchor(type: Anclaje): THREE.Mesh {
  switch (type) {
    case 'hormigon': {
      const m = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.08, 0.3), createConcreteMaterial());
      m.position.y = 0.04;
      m.castShadow = true;
      m.receiveShadow = true;
      m.name = 'anchor-hormigon';
      return m;
    }
    case 'tarima': {
      const m = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.02, 0.24), createSteelMaterial());
      m.position.y = 0.01;
      m.castShadow = true;
      m.receiveShadow = true;
      m.name = 'anchor-tarima';
      return m;
    }
    case 'cesped': {
      // Pica/zapata de acero parcialmente visible sobre el césped.
      const m = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.05, 0.18, 12), createSteelMaterial());
      m.position.y = 0.09;
      m.castShadow = true;
      m.receiveShadow = true;
      m.name = 'anchor-cesped';
      return m;
    }
  }
}
